// src/components/AppErrorBoundary.tsx
import React, { ErrorInfo, ReactNode } from "react";
import { logger } from "../lib/logger";

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
}

export class AppErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Try to use logger, but fallback to console if logger isn't available yet
        // (since this boundary wraps providers, logger setup might not be complete)
        try {
            logger.error("App-level error boundary caught an error", error, {
                componentStack: errorInfo.componentStack,
                errorBoundary: "AppErrorBoundary",
                level: "app-level",
            });
        } catch (loggerError) {
            // Fallback if logger fails (e.g., Convex client not initialized)
            console.error("App-level error boundary caught an error", {
                error: {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                },
                errorInfo: {
                    componentStack: errorInfo.componentStack,
                },
            });
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
                    <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                        <div className="text-center">
                            <div className="text-red-500 text-6xl mb-4">⚠️</div>
                            <h2 className="text-xl font-subheader text-gray-900 dark:text-gray-100 mb-2">
                                Application Error
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                                A critical error occurred in the application. Please refresh the page to try again.
                            </p>
                            {this.state.error && process.env.NODE_ENV === "development" && (
                                <details className="mb-4 text-left">
                                    <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400 mb-2">
                                        Error Details (Dev Only)
                                    </summary>
                                    <pre className="text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-auto max-h-40">
                                        {this.state.error.toString()}
                                        {this.state.error.stack && `\n${this.state.error.stack}`}
                                    </pre>
                                </details>
                            )}
                            <button
                                onClick={() => window.location.reload()}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
                            >
                                Refresh Page
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
